import { useEffect, useState } from 'react';

import { Flex, FloraButton, Toaster } from '@grupoboticario/flora-react';
import {
  BarcodeFillIcon,
  CalendarCrossIcon,
  ClipboardTextIcon,
  DocumentSingleIcon,
  InformationCircleIcon,
  ExclamationMarkHexagonIcon,
  PixOutlineIcon,
  TrayArrowDownIcon,
} from '@grupoboticario/flora-react-icons';
import { Link } from 'react-router-dom';

import { ResponsiveFloraTypography, Tag } from 'components';
import config from 'config';
import { FeatureFlag } from 'domains/feature-flag.d';
import { PaymentType } from 'domains/payment-titles.d';
import {
  copyText,
  getFormattedPrice,
  getLongDate,
  getPaymentStatus,
  isMobile as isMobileHelper,
} from 'helpers';
import { usePaymentDetail, useDrawer, useTracking, useFeatureFlag } from 'hooks';
import { newRelicMonitoringService } from 'services';

import Styles from './PaymentDetail.components.styles';
import DrawerStyles from '../../Drawers.styles';

export const Details = () => {
  const [brand, setBrand] = useState(config.app.slugname);
  const { searchParams } = useDrawer('PaymentDetail');
  const { sendDataToGTM } = useTracking();
  const isMobile = isMobileHelper();
  const { data, isEudoraVdfPayment } = usePaymentDetail(searchParams.get('paymentId') ?? '');
  const isBoleto = data?.paymentType === PaymentType.Boleto;
  const isEnvironmentEudora = config.app.slugname === 'eudora';
  const { value: isVDFPaymentEnable, loading: isLoadingVDFFeatureFlag } = useFeatureFlag(
    FeatureFlag.ENABLE_VDF_PAYMENT,
    false
  );

  const handleDownload2aVia = () => {
    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_detalhes_pagamento',
        cd_interaction_detail: isBoleto
          ? `${brand}:click:baixar-segunda-via`
          : `${brand}:click:baixar-qr-code`,
        transaction_id: data?.orderId,
        cd_metodo_pagamento: data?.planDetail.toLowerCase(),
        cd_status_pagamento: paymentStatus?.tag.toLowerCase(),
        cd_dias_para_vencimento: data?.daysUntilDueDate,
        cd_dias_apos_vencimento: data?.daysLate,
      },
    });
  };

  const handleCopy = () => {
    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_detalhes_pagamento',
        cd_interaction_detail: isBoleto
          ? `${brand}:click:copiar-codigo`
          : `${brand}:click:pix-copia-e-cola`,
        transaction_id: data?.orderId,
        cd_metodo_pagamento: data?.planDetail.toLowerCase(),
        cd_status_pagamento: paymentStatus?.tag.toLowerCase(),
        cd_dias_para_vencimento: data?.daysUntilDueDate,
        cd_dias_apos_vencimento: data?.daysLate,
      },
    });

    if (data) {
      const message = isBoleto ? 'do boleto' : 'Pix';

      copyText(data.barCode)
        .then(() => {
          Toaster.notify({
            kind: 'success',
            description: `Código ${message} copiado com sucesso!`,
            origin: 'right-top',
          });
        })
        .catch((e) => {
          Toaster.notify({
            kind: 'error',
            description: `Não foi possível copiar o código ${message}. Por favor, tente novamente.`,
            origin: 'right-top',
          });

          newRelicMonitoringService.addPageAction('callback_copiar_codigo', {
            cd_interaction_detail: `erro:${e.message}`,
            cd_numero_pedido: searchParams.get('paymentId'),
          });
        });
    }
  };

  const paymentStatus = getPaymentStatus({
    daysLate: data?.daysLate || 0,
    daysUntilDueDate: data?.daysUntilDueDate || 0,
    paymentStatus: data?.paymentStatus || '',
    status: data?.status || '',
  });

  const hasConditionStatus =
    Boolean(paymentStatus?.tag === 'Em aberto') || Boolean(paymentStatus?.tag === 'Em atraso');

  useEffect(() => {
    if (isVDFPaymentEnable && !isLoadingVDFFeatureFlag) {
      setBrand(isEudoraVdfPayment ? 'eudora' : 'boticario');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingVDFFeatureFlag]);

  return (
    <>
      <DrawerStyles.DrawerSection align='flex-start' direction='column' gap='$4'>
        {paymentStatus?.tag === 'Em aberto' || paymentStatus?.tag === 'Em atraso' ? (
          <Styles.FixedToast variant='active' data-card>
            <Flex gap='$2' align='center'>
              <InformationCircleIcon />
              <ResponsiveFloraTypography fontSize='exceptionsAuxiliar'>
                {paymentStatus?.tag === 'Em aberto'
                  ? 'Após o pagamento, a atualização do status pode levar até 2 dias úteis.'
                  : 'O pagamento do título atrasado pode ser feito com o mesmo documento ou código de barras. Se houver juros, serão calculados e aplicados na hora do pagamento.'}
              </ResponsiveFloraTypography>
            </Flex>
          </Styles.FixedToast>
        ) : null}

        <Styles.Divider variant='Line' />

        <Flex gap='$2' direction='column'>
          <ResponsiveFloraTypography fontSize='subtitle' fontWeight='bold' as='h2'>
            {getFormattedPrice(data?.amount || 0)}
          </ResponsiveFloraTypography>
        </Flex>

        <Styles.Divider variant='Line' />

        <Styles.Detail justify='space-between'>
          <Flex gap='$2'>
            <ClipboardTextIcon size={20} />
            <ResponsiveFloraTypography fontSize='bodySmallStandard' aria-hidden>
              Status do pagamento
            </ResponsiveFloraTypography>
          </Flex>

          <ResponsiveFloraTypography fontSize='bodySmallStandard'>
            {paymentStatus && paymentStatus.tagVariant && (
              <Tag
                data={{
                  ...paymentStatus,
                  tagText: paymentStatus.tag,
                  subTitleText: paymentStatus.subTitle || '',
                }}
                type='pagamento'
              />
            )}
          </ResponsiveFloraTypography>
        </Styles.Detail>

        <Styles.Detail justify='space-between'>
          <Flex align='center' gap='$2'>
            <CalendarCrossIcon size={22} />
            <ResponsiveFloraTypography fontSize='bodySmallStandard'>
              {paymentStatus?.subTitle}
            </ResponsiveFloraTypography>
          </Flex>

          <ResponsiveFloraTypography fontSize='bodySmallStandard'>
            {getLongDate(data?.dueDate || '')}
          </ResponsiveFloraTypography>
        </Styles.Detail>

        {isEudoraVdfPayment && !isEnvironmentEudora && isVDFPaymentEnable && (
          <Styles.Tag>
            <ExclamationMarkHexagonIcon size={24} />
            <ResponsiveFloraTypography fontSize='exceptionsAuxiliar'>
              Trouxemos seus pagamentos pendentes de Eudora! Continue comprando através deste
              portal.
            </ResponsiveFloraTypography>
          </Styles.Tag>
        )}

        {hasConditionStatus ? (
          <Flex
            justify='space-between'
            gap='$3'
            direction={isMobile ? 'column-reverse' : 'row'}
            css={{ width: '100%' }}
          >
            <FloraButton
              as={Link}
              data-id={data?.orderId}
              isFull
              onClick={handleDownload2aVia}
              to={data?.fileLink || ''}
              has='iconRight'
              icon={isBoleto ? <DocumentSingleIcon /> : <TrayArrowDownIcon />}
              role='button'
              target='_blank'
              hierarchy='secondary'
              rel='noopener noreferrer'
            >
              <ResponsiveFloraTypography fontSize='bodySmallShort' fontWeight='bold'>
                {isBoleto ? `Baixar segunda via` : 'Baixar QR Code'}
              </ResponsiveFloraTypography>
            </FloraButton>

            <FloraButton
              aria-label={isBoleto ? `Copiar código do Boleto` : 'Copiar código do Pix'}
              data-id={data?.orderId}
              onClick={handleCopy}
              isFull
              has='iconRight'
              icon={isBoleto ? <BarcodeFillIcon /> : <PixOutlineIcon />}
              role='button'
              hierarchy='primary'
              rel='noopener noreferrer'
            >
              <ResponsiveFloraTypography fontSize='bodySmallShort' fontWeight='bold'>
                {isBoleto ? `Copiar código` : `Pix copia e cola`}
              </ResponsiveFloraTypography>
            </FloraButton>
          </Flex>
        ) : null}
      </DrawerStyles.DrawerSection>
    </>
  );
};
